export enum BroadbandBillingTerm {
  NO_LOCK_IN = 'NO_LOCK_IN',
  MONTHS_12 = 'MONTHS_12',
  MONTHS_24 = 'MONTHS_24',
}

export interface IBroadbandPlanApiResponse {
  data: IBroadbandPlans;
}

/**
 * This is the response from the broadband plans api
 * (under the "data" property in the response)
 */
export interface IBroadbandPlans {
  plans: IBroadbandPlan[];
  promoTermMonths: string;
  singleFuelDiscount: string;
  employeeDiscount: string;
  optiCommConnectionFees: string;
  addressId: string;
  address: string;
  type: string;
  additionalData?: IBroadbandAdditionalData;
  offerCodeValidation?: OfferCodeValidationResponse;
  structuredAddress: IBroadbandAddress;
  promoConfig?: IPlanPromoConfig;
}

/**
 * This represents an individual plan coming back from the broadband plans API.
 */
export interface IBroadbandPlan {
  planId: string;
  planType: string;
  planName: string;
  zoneName: string;
  description: string;
  templateId: string;
  edrEligiblePoints: string;
  edrOfferID: string;
  promotionId?: string;
  term: BroadbandBillingTerm;
  planTerms: IPlanTerms[];
  planMetaData?: INewPlanMetaData;
}

/**
 * This is the payload we save to the cart.  It represents the plan selected by the customer.
 *
 * It's really just the response from BB plans api but rather than plans containing an array of
 * plans, we replace it with just the selected plan
 */
export type IBroadbandOffer = Omit<IBroadbandPlans, 'plans' | 'offerCodeValidation'> & {
  plan: IBroadbandPlan;
  dynamicOffer?: IDynamicOffer;
  fibreUpgradeReady?: boolean;
};

export interface IBroadbandAdditionalData {
  fibreUpgradeReady?: boolean;
}

export interface OfferCodeValidationResponse {
  isValid: boolean;
  offer?: IDynamicOffer;
}

export interface INewPlanMetaData {
  activeService: string;
  connectionType: string;
  newDevelopmentFeeApplies: boolean;
  newDevelopmentFeeAmount?: number;
  newConnectionFeeApplies: boolean;
  newConnectionFeeAmount?: number;
  serviceClass: string;
  speedTier: string;
  technologyType: string;
  typicalConnectionTime: string;
  uploadSpeedTier: string;
}

export interface IPlanTerms {
  billingTerm: keyof typeof BroadbandBillingTerm;
  speedTier: string;
  uploadSpeedTier?: string;
  typicalSpeed: string;
  typicalSpeedUpload?: string;
  monthlyCost: number;
  promoCode: string;
  monthlyPromoDiscount: number;
  monthlyCostSingleFuelDiscounted: number;
  modemCost: IBroadbandPricingItem[];
  modemDeliveryCost: IBroadbandPricingItem[];
  totalMinCost: number;
  monthlyBundleDiscount?: number;
  subscriptionEligible?: boolean;
}

export type IBroadbandPricingItem = {
  label: string;
  pricing: number;
  value: string;
};

export interface IDynamicOffer {
  offerCode: string;
  discountValue: number;
  termMonths: number;
  providerPromoCode: string;
}

export interface IBroadbandAddress {
  unit: string;
  streetNumber: string;
  street: string;
  streetType: string;
  city: string;
  state: string;
  postcode: string;
  dpid: string;
}

export interface IPlanPromoConfig {
  promotionId: string;
  isBundled: boolean;
  hasBundleToggle: boolean;
  hideCrossSell: boolean;
  discountPercentage?: number;
  creditValue?: number;
  creditAppliedDate?: string;
}

export enum ModemCode {
  TP_LINK = 'TPLINK',
  EERO = 'AMAZON_EERO7',
  FREE_TP_LINK = 'TPLINK_FREE',
  FREE_EERO = 'AMAZON_EERO7_FREE',
}
